import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js';

/** Countries the portal's patients most often have, first in the picker. */
const FIRST: CountryCode[] = ['EG', 'SA', 'AE', 'KW', 'QA', 'BH', 'OM', 'JO', 'LB', 'IQ', 'LY', 'SD', 'GB', 'US'];

export interface CountryOption {
  code: CountryCode;
  dial: string;
  name: string;
  flag: string;
}

/** 🇪🇬 from "EG" — regional indicator letters. */
export const flagOf = (code: string) =>
  code.toUpperCase().replace(/./g, (c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65));

export function countryOptions(lang: string, first: string): CountryOption[] {
  let names: Intl.DisplayNames | null = null;
  try {
    names = new Intl.DisplayNames([lang], { type: 'region' });
  } catch {
    names = null;
  }
  const all = getCountries().map((code) => ({
    code,
    dial: getCountryCallingCode(code),
    name: names?.of(code) ?? code,
    flag: flagOf(code),
  }));
  const head = [first, ...FIRST.filter((c) => c !== first)];
  const byCode = new Map(all.map((c) => [c.code as string, c]));
  const top = head.map((c) => byCode.get(c)).filter((c): c is CountryOption => !!c);
  const rest = all
    .filter((c) => !head.includes(c.code))
    .sort((a, b) => a.name.localeCompare(b.name, lang));
  return [...top, ...rest];
}

export function isCountry(v: unknown): v is CountryCode {
  return typeof v === 'string' && (getCountries() as string[]).includes(v);
}

/** E.164 for a national number typed under `country`; null when it is not one. */
export function toE164(national: string, country: CountryCode): string | null {
  const raw = national.trim();
  if (!raw) return null;
  const p = raw.startsWith('+') || raw.startsWith('00')
    ? parsePhoneNumberFromString(raw.replace(/^00/, '+'))
    : parsePhoneNumberFromString(raw, country);
  return p?.isValid() ? p.number : null;
}

/** "+20 100 123 4567" → { country: 'EG', national: '100 123 4567' } (for an existing value). */
export function splitE164(value: string, fallback: CountryCode): { country: CountryCode; national: string } {
  const p = value ? parsePhoneNumberFromString(value.startsWith('+') ? value : `+${value}`) : undefined;
  if (p?.country) return { country: p.country, national: p.formatNational() };
  return { country: fallback, national: value };
}

/** Live formatting while typing, in the chosen country's pattern. */
export function formatAsYouType(national: string, country: CountryCode): string {
  if (national.trim().startsWith('+')) return new AsYouType().input(national);
  return new AsYouType(country).input(national);
}

/** The country a pasted "+…" number belongs to, if any. */
export function countryOfInternational(text: string): CountryCode | undefined {
  const t = text.trim().replace(/^00/, '+');
  if (!t.startsWith('+')) return undefined;
  return parsePhoneNumberFromString(t)?.country;
}
